import { Controller } from "@hotwired/stimulus"

// Главный контроллер страницы LLM Settings.
// Управляет: табами, URL, keyboard nav, summary bar, bulk-select,
// master-detail для шаблонов, синхронизацией hidden inputs.
export default class extends Controller {
  static targets = [
    "tabButton", "tabPanel",
    "summaryCount", "summaryDetails", "summaryToggle", "headerCount",
    "templateDetailPanel", "templateDetailTitle", "templateDetailCode",
    "templateDetailRead", "templateDetailWrite", "templateDetailStructure",
    "hiddenFormContainer"
  ]

  static values = {
    activeTab: { type: String, default: "general" }
  }

  connect() {
    this.activateTab(this.tabFromUrl() || this.activeTabValue)
    this.updateSummary()
    this.bindCheckboxListeners()
  }

  // ============================================================
  // Tabs + URL + ARIA
  // ============================================================

  switchTab(event) {
    const tab = event.currentTarget.dataset.tab
    this.activateTab(tab)
  }

  activateTab(tab) {
    this.tabPanelTargets.forEach((panel) => {
      const isActive = panel.dataset.tab === tab
      panel.classList.toggle("hidden", !isActive)
      if (isActive) panel.removeAttribute("hidden")
    })

    this.tabButtonTargets.forEach((btn) => {
      const isActive = btn.dataset.tab === tab
      btn.setAttribute("aria-selected", isActive ? "true" : "false")
      btn.setAttribute("tabindex", isActive ? "0" : "-1")

      // visual styles
      if (isActive) {
        btn.classList.add("border-indigo-500", "text-indigo-600")
        btn.classList.remove("border-transparent", "text-slate-500", "hover:text-slate-700", "hover:border-slate-300")
      } else {
        btn.classList.remove("border-indigo-500", "text-indigo-600")
        btn.classList.add("border-transparent", "text-slate-500", "hover:text-slate-700", "hover:border-slate-300")
      }
    })

    this.updateUrl(tab)
  }

  tabFromUrl() {
    const params = new URLSearchParams(window.location.search)
    const tab = params.get("tab")
    const valid = this.tabButtonTargets.some((b) => b.dataset.tab === tab)
    return valid ? tab : null
  }

  updateUrl(tab) {
    const url = new URL(window.location.href)
    url.searchParams.set("tab", tab)
    history.replaceState({}, "", url)
  }

  // ============================================================
  // Keyboard navigation (tablist)
  // ============================================================

  handleKeydown(event) {
    const buttons = this.tabButtonTargets
    const index = buttons.findIndex((b) => b.getAttribute("aria-selected") === "true")
    if (index === -1) return

    let newIndex = index

    switch (event.key) {
      case "ArrowRight":
        newIndex = (index + 1) % buttons.length
        break
      case "ArrowLeft":
        newIndex = (index - 1 + buttons.length) % buttons.length
        break
      case "Home":
        newIndex = 0
        break
      case "End":
        newIndex = buttons.length - 1
        break
      default:
        return
    }

    event.preventDefault()
    buttons[newIndex].focus()
    this.activateTab(buttons[newIndex].dataset.tab)
  }

  // ============================================================
  // Summary bar
  // ============================================================

  updateSummary() {
    const models = this.countChecked('input[name="llm_setting[allowed_model_codes][]"]')
    const templates = this.countChecked('input[name="llm_setting[allowed_item_template_codes][]"]')
    const sources = this.countChecked('input[name="llm_setting[allowed_source_codes][]"]')
    const tools = this.countChecked('input[name="llm_setting[allowed_tool_names][]"]')
    const singles = this.countChecked('input[name^="llm_setting[single_policies]"][name$="[ai_writable]"]')

    const text = [`${models} моделей`, `${templates} шаблонов`, `${sources} источников`, `${tools} инструментов`]
    if (singles > 0) text.push(`${singles} single`)

    const summaryText = text.join(" · ")
    this.summaryCountTarget.textContent = summaryText
    if (this.hasHeaderCountTarget) {
      this.headerCountTarget.textContent = summaryText
    }
    this.renderSummaryDetails()
  }

  countChecked(selector) {
    return this.element.querySelectorAll(selector + ":checked").length
  }

  toggleSummaryDetails() {
    this.summaryDetailsTarget.classList.toggle("hidden")
    const expanded = !this.summaryDetailsTarget.classList.contains("hidden")
    this.summaryToggleTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
  }

  renderSummaryDetails() {
    const container = this.summaryDetailsTarget.querySelector("[data-summary-list]")
    if (!container) return

    const collect = (sel, attr) =>
      Array.from(this.element.querySelectorAll(sel + ":checked")).map((el) => {
        const row = el.closest("[data-search-key]")
        return row ? row.dataset.searchKey : (el.value || "")
      })

    const models = collect('input[name="llm_setting[allowed_model_codes][]"]')
    const templates = collect('input[name="llm_setting[allowed_item_template_codes][]"]')
    const sources = collect('input[name="llm_setting[allowed_source_codes][]"]')
    const tools = collect('input[name="llm_setting[allowed_tool_names][]"]')

    let html = ""
    if (models.length) html += `<div class="mb-2"><div class="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Модели</div><div class="text-sm text-slate-700">${models.join(", ")}</div></div>`
    if (templates.length) html += `<div class="mb-2"><div class="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Шаблоны</div><div class="text-sm text-slate-700">${templates.join(", ")}</div></div>`
    if (sources.length) html += `<div class="mb-2"><div class="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Источники</div><div class="text-sm text-slate-700">${sources.join(", ")}</div></div>`
    if (tools.length) html += `<div class="mb-2"><div class="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Инструменты</div><div class="text-sm text-slate-700">${tools.join(", ")}</div></div>`

    container.innerHTML = html || '<div class="text-sm text-slate-500">Ничего не выбрано</div>'
  }

  // ============================================================
  // Bulk select
  // ============================================================

  toggleGroup(event) {
    const group = event.currentTarget.closest("[data-prefix-group]")
    if (!group) return
    const checked = event.currentTarget.checked
    const checkboxes = group.querySelectorAll('input[type="checkbox"]')
    checkboxes.forEach((cb) => {
      if (cb !== event.currentTarget && !cb.closest("[data-prefix-header]")) {
        cb.checked = checked
      }
    })
    this.updateSummary()
  }

  selectVisible(event) {
    const scope = event.currentTarget.dataset.selectScope
    const panel = this.tabPanelTargets.find((p) => p.dataset.tab === scope)
    if (!panel) return
    panel.querySelectorAll('[data-search-key]:not(.hidden) input[type="checkbox"]').forEach((cb) => {
      cb.checked = true
    })
    this.updateSummary()
  }

  deselectAll(event) {
    const scope = event.currentTarget.dataset.selectScope
    const panel = this.tabPanelTargets.find((p) => p.dataset.tab === scope)
    if (!panel) return
    panel.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
      cb.checked = false
    })
    this.updateSummary()
  }

  // ============================================================
  // Master-detail (Templates tab)
  // ============================================================

  showTemplateDetail(event) {
    const row = event.currentTarget.closest("[data-template-code]")
    if (!row) return
    const code = row.dataset.templateCode
    const name = row.dataset.templateName

    this.templateDetailCodeTarget.value = code
    this.templateDetailTitleTarget.textContent = name

    const read = this.hiddenValue(`llm_setting[template_policies][${code}][read]`)
    const write = this.hiddenValue(`llm_setting[template_policies][${code}][write]`)
    const structure = this.hiddenValue(`llm_setting[template_policies][${code}][structure]`)

    this.templateDetailReadTarget.checked = read === "1"
    this.templateDetailWriteTarget.checked = write === "1"
    this.templateDetailStructureTarget.checked = structure === "1"

    this.templateDetailPanelTarget.classList.remove("hidden")
  }

  applyTemplatePolicy() {
    const code = this.templateDetailCodeTarget.value
    if (!code) return

    const read = this.templateDetailReadTarget.checked
    const write = this.templateDetailWriteTarget.checked
    const structure = this.templateDetailStructureTarget.checked

    this.setHiddenValue(`llm_setting[template_policies][${code}][read]`, read ? "1" : "0")
    this.setHiddenValue(`llm_setting[template_policies][${code}][write]`, write ? "1" : "0")
    this.setHiddenValue(`llm_setting[template_policies][${code}][structure]`, structure ? "1" : "0")

    const row = this.findRowByTemplateCode(code)
    if (row) {
      const any = read || write || structure
      const checkbox = row.querySelector('input[name="llm_setting[allowed_item_template_codes][]"]')
      if (checkbox) checkbox.checked = any
      row.classList.toggle("border-indigo-300", any)
      row.classList.toggle("bg-indigo-50", any)
      row.classList.toggle("border-slate-200", !any)
      this.updatePolicyBadges(row, read, write, structure)
    }

    this.updateSummary()
  }

  applyToGroup() {
    const code = this.templateDetailCodeTarget.value
    if (!code) return

    const read = this.templateDetailReadTarget.checked
    const write = this.templateDetailWriteTarget.checked
    const structure = this.templateDetailStructureTarget.checked

    this.element.querySelectorAll("[data-template-code]").forEach((el) => {
      const rowCode = el.dataset.templateCode
      if (this.samePrefix(rowCode, code)) {
        this.setHiddenValue(`llm_setting[template_policies][${rowCode}][read]`, read ? "1" : "0")
        this.setHiddenValue(`llm_setting[template_policies][${rowCode}][write]`, write ? "1" : "0")
        this.setHiddenValue(`llm_setting[template_policies][${rowCode}][structure]`, structure ? "1" : "0")

        const row = el.closest("[data-template-row]")
        if (!row) return
        const any = read || write || structure
        const checkbox = row.querySelector('input[name="llm_setting[allowed_item_template_codes][]"]')
        if (checkbox) checkbox.checked = any
        row.classList.toggle("border-indigo-300", any)
        row.classList.toggle("bg-indigo-50", any)
        row.classList.toggle("border-slate-200", !any)
        this.updatePolicyBadges(row, read, write, structure)
      }
    })

    this.updateSummary()
  }

  hideTemplateDetail() {
    this.templateDetailPanelTarget.classList.add("hidden")
    this.templateDetailCodeTarget.value = ""
  }

  // ============================================================
  // Helpers
  // ============================================================

  hiddenValue(name) {
    const inputs = this.hiddenFormContainerTarget.querySelectorAll('input[type="hidden"]')
    const found = Array.from(inputs).find((i) => i.name === name)
    return found ? found.value : "0"
  }

  setHiddenValue(name, value) {
    const inputs = this.hiddenFormContainerTarget.querySelectorAll('input[type="hidden"]')
    const found = Array.from(inputs).find((i) => i.name === name)
    if (found) found.value = value
  }

  findRowByTemplateCode(code) {
    const el = Array.from(this.element.querySelectorAll("[data-template-code]")).find(
      (row) => row.dataset.templateCode === code
    )
    return el ? el.closest("[data-template-row]") : null
  }

  samePrefix(a, b) {
    const pa = this.extractPrefix(a)
    const pb = this.extractPrefix(b)
    return pa && pb && pa === pb
  }

  updatePolicyBadges(row, read, write, structure) {
    const readBadge = row.querySelector("[data-policy-read]")
    const writeBadge = row.querySelector("[data-policy-write]")
    const structureBadge = row.querySelector("[data-policy-structure]")

    if (readBadge) {
      readBadge.className = `text-[9px] font-bold px-1 py-0.5 rounded ${read ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-400"}`
    }
    if (writeBadge) {
      writeBadge.className = `text-[9px] font-bold px-1 py-0.5 rounded ${write ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`
    }
    if (structureBadge) {
      structureBadge.className = `text-[9px] font-bold px-1 py-0.5 rounded ${structure ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-400"}`
    }
  }

  extractPrefix(code) {
    const s = code.toString()
    if (s.startsWith("sys.")) return "sys"
    if (s.startsWith("dem.")) return "dem"
    if (s.startsWith("Oracle")) return "Oracle"
    if (s.startsWith("RS")) return "RS"
    if (s.startsWith("user_")) return "user"
    const dot = s.indexOf(".")
    const usc = s.indexOf("_")
    const idx = dot === -1 ? usc : usc === -1 ? dot : Math.min(dot, usc)
    return idx === -1 ? s : s.slice(0, idx)
  }

  bindCheckboxListeners() {
    // Любой чекбокс внутри страницы — обновляет summary
    this.element.addEventListener("change", (e) => {
      if (e.target.matches('input[type="checkbox"]')) {
        this.updateSummary()
      }
    })
  }

  // ============================================================
  // Template checkbox toggle — clears policies when unchecked
  // ============================================================

  toggleTemplateCheckbox(event) {
    const checkbox = event.currentTarget
    const code = checkbox.dataset.templateCode
    if (!code) return

    const checked = checkbox.checked
    const row = checkbox.closest("[data-template-row]")

    if (!checked) {
      // Сняли галочку — очищаем template_policies для этого шаблона
      this.setHiddenValue(`llm_setting[template_policies][${code}][read]`, "0")
      this.setHiddenValue(`llm_setting[template_policies][${code}][write]`, "0")
      this.setHiddenValue(`llm_setting[template_policies][${code}][structure]`, "0")

      // Обновляем визуальное состояние
      if (row) {
        row.classList.remove("border-indigo-300", "bg-indigo-50")
        row.classList.add("border-slate-200")
        this.updatePolicyBadges(row, false, false, false)
      }
    } else {
      // Поставили галочку — устанавливаем дефолтную policy (read: true, write: true, structure: false)
      this.setHiddenValue(`llm_setting[template_policies][${code}][read]`, "1")
      this.setHiddenValue(`llm_setting[template_policies][${code}][write]`, "1")
      this.setHiddenValue(`llm_setting[template_policies][${code}][structure]`, "0")

      // Обновляем визуальное состояние
      if (row) {
        row.classList.add("border-indigo-300", "bg-indigo-50")
        row.classList.remove("border-slate-200")
        this.updatePolicyBadges(row, true, true, false)
      }
    }

    this.updateSummary()
  }
}
